import { useEffect, useState, useCallback } from 'react';
import type { FC, ReactNode } from 'react';
import Cookie from 'js-cookie';
import { defineMessages, IntlProvider } from 'react-intl';
import moment from 'moment';
import useAccountStore from 'stores/useAccountStore';
import sameVersion from 'stores/compare/versions';
import { localeIsSupported, loadLocale } from 'i18n/locales';
import type { SupportedLocale, Messages } from 'i18n/locales';
import 'i18n/moment-config';

const DEFAULT_LOCALE: SupportedLocale = 'en';

const localeNames = defineMessages({
  'zh-TW': {
    id: 'i18n.chinese',
    defaultMessage: 'Chinese (中文)',
  },
  en: {
    id: 'i18n.english',
    defaultMessage: 'English',
  },
  ko: {
    id: 'i18n.korean',
    defaultMessage: 'Korean (한글)',
  },
  ja: {
    id: 'i18n.japanese',
    defaultMessage: 'Japanese (日本語)',
  },
});

function findSupportedBrowserLocale(): SupportedLocale {
  // eslint-disable-next-line no-restricted-syntax
  for (const languageTag of navigator.languages) {
    const primaryLanguageSubtag = languageTag.split('-')[0];
    if (localeIsSupported(primaryLanguageSubtag)) {
      return primaryLanguageSubtag;
    }
  }

  return DEFAULT_LOCALE;
}

const LOCALE_COOKIE = 'locale';

const readFromCookie = (): string | undefined => {
  return Cookie.get(LOCALE_COOKIE);
};

const saveToCookie = (locale: SupportedLocale) => {
  Cookie.set(LOCALE_COOKIE, locale);
};

const removeCookie = () => {
  Cookie.remove(LOCALE_COOKIE);
};

const LanguageProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [locale, setLocale] = useState<SupportedLocale | undefined>();
  const [messages, setMessages] = useState<Messages | undefined>();
  const user = useAccountStore(({ account }) => account, sameVersion);

  const applyLocale = useCallback(async (newLocale: SupportedLocale) => {
    const msgs = await loadLocale(newLocale);
    moment.locale(newLocale);
    setMessages(msgs);
    setLocale(newLocale);
  }, []);

  const applyBrowserDefault = useCallback(() => {
    const browserLocale = findSupportedBrowserLocale();
    applyLocale(browserLocale);
    removeCookie();
  }, [applyLocale]);

  useEffect(() => {
    if (!user) {
      // We bother with a cookie because it allows us to persist the language during a
      //  session and avoid waiting the flash due to waiting for the profile data to load.
      const cookiedLocale = readFromCookie();
      if (cookiedLocale && localeIsSupported(cookiedLocale)) {
        applyLocale(cookiedLocale);
      } else applyBrowserDefault();
      return;
    }

    const { language } = user;
    if (!language) {
      applyBrowserDefault();
    } else if (localeIsSupported(language)) {
      applyLocale(language);
      saveToCookie(language);
    }
  }, [user, applyBrowserDefault, applyLocale]);

  useEffect(() => {
    const changeListener = () => {
      if (!user?.language) applyBrowserDefault();
    };

    window.addEventListener('languagechange', changeListener);

    return () => {
      window.removeEventListener('languagechange', changeListener);
    };
  }, [applyBrowserDefault, user]);

  return (
    locale && messages !== undefined && (
      <IntlProvider locale={locale} defaultLocale={DEFAULT_LOCALE} messages={messages}>
        {children}
      </IntlProvider>
    )
  );
};

export default LanguageProvider;
export { localeNames };
export type { SupportedLocale };
